import PostCard, { postCardRIVProps } from '@/Components/posts/ui/posts-card';
import RenderIfVisible from '@/Utility/RenderIfVisible';
import type { ZnglyVideo } from '@/graphql/types';
import { useAppStore } from '@/inertia/AppStore';
import { onSelectPlayerNode } from '@/inertia/Shared/Catalog/catalog.utils';
import { Box, Grid2, LinearProgress } from '@mui/material';
import { Fragment } from 'react';
import { PAGE_LAYOUT } from '../../Page';
import Share from '../Share';
import { Bar } from '../SideBar';

type Props = {
  showShare?: boolean;
  showBar?: boolean;
  isFallback?: boolean;
};

export const GridView = ({ showShare = true, showBar = true, isFallback }: Props) => {
  const nodes = useAppStore((state) => state?.catalogNodes);
  const playerNode = useAppStore((state) => state?.playerNode);

  const catalogLoadMore = useAppStore((state) => state?.catalogLoadMore);
  const isCatalogLoadingMore = useAppStore((state) => state?.isCatalogLoadingMore);

  return (
    <>
      {showShare && (
        <>
          <Grid2 {...PAGE_LAYOUT.leftProps}>{/*  */}</Grid2>
          <Grid2 {...PAGE_LAYOUT.rightProps}>
            <Share isFallback={isFallback} />
          </Grid2>
        </>
      )}

      <Box width={'100%'} mb={2} className={'GridView-Wrapper FormGating_GridView'}>
        {showBar && (
          <Grid2 size={{ sm: 12 }} mb={0} p={0}>
            <Bar isFallback={isFallback} />
          </Grid2>
        )}

        <Grid2
          container
          columns={12}
          size={{ sm: 12 }}
          sx={{ border: '1px solid #e0e0e0' }}
          className="GridView-Container"
        >
          {isCatalogLoadingMore && (
            <LinearProgress sx={{ width: '100%', height: '3px', position: 'sticky', top: '0', zIndex: 100 }} />
          )}
          <AiSummary />
          {nodes?.map((node, idx) => {
            return (
              <RenderIfVisible
                key={`gridview_${node?.id ?? idx}`}
                {...postCardRIVProps(node, 'catalog', idx)}
                onChange={(isVisible) => {
                  if (isVisible && idx === nodes.length - 1) {
                    catalogLoadMore?.();
                  }
                }}
              >
                <PostCard
                  type="catalog"
                  fallback={isFallback}
                  key={node?.id}
                  node={node}
                  disabled={node?.id === playerNode?.id}
                  onClick={(node) => {
                    onSelectPlayerNode(node as ZnglyVideo, idx);
                  }}
                />
              </RenderIfVisible>
            );
          })}
        </Grid2>
      </Box>
    </>
  );
};

const CitationRegex = /\[\s*\d+\s*(?:,\s*\d+\s*)*\]/g;

const AiSummary = () => {
  const nodes = useAppStore((state) => state?.catalogNodes);
  const setPlayerNode = useAppStore((state) => state.setPlayerNode);
  const setBarView = useAppStore((state) => state.setBarView);

  const vertexSearchAgent = useAppStore((state) => state?.vertexSearchAgent);

  if (!vertexSearchAgent?.summary?.summaryText) return null;

  const summary = vertexSearchAgent?.summary?.summaryText;

  // look through the summary for citations in the format: [1] [2] etc...
  // then return the new react element with the text & citation links
  const parsedText = summary?.split(CitationRegex).map((text, idx) => {
    const citation = summary?.match(CitationRegex)?.[idx];
    const citationNumber = Number.parseInt(citation?.replace(/\D/g, '') ?? '');
    const node = nodes?.length >= citationNumber ? nodes[citationNumber - 1] : null;

    if (!citation || !citationNumber || !node)
      return (
        // biome-ignore lint/suspicious/noArrayIndexKey: Not enough context to provide a unique key
        <Fragment key={`${text}_${idx}`}>
          <span dangerouslySetInnerHTML={{ __html: text }} />
        </Fragment>
      );

    return (
      // biome-ignore lint/suspicious/noArrayIndexKey: Not enough context to provide a unique key
      <Fragment key={`${text}_${idx}`}>
        <span dangerouslySetInnerHTML={{ __html: text }} />
        <a
          href={node?.link ?? '#'}
          target="_blank"
          rel="noopener noreferrer"
          key={citationNumber}
          onClick={(e) => {
            e.preventDefault();
            setPlayerNode(node);
            setBarView('list');
          }}
          style={{ color: 'var(--z-brand-clr-0)' }}
        >
          {citation}
        </a>
      </Fragment>
    );
  });

  return (
    <Grid2 size={{ xs: 12 }} sx={{ padding: 1 }}>
      {parsedText}
    </Grid2>
  );
};

export default GridView;
